/**
 * Manages terminal output buffer for error detection
 * Provides methods for buffer management and error line extraction
 */
import { cleanTerminalOutput } from '~/utils/shell';

export class ErrorBuffer {
  private buffer: string = '';
  private maxBufferSize: number = 10000; // Maximum buffer size to prevent memory issues

  /**
   * Add content to the buffer
   * @param content The content to add to the buffer
   */
  append(content: string): void {
    this.buffer += content;

    // Trim buffer if it exceeds maximum size
    if (this.buffer.length > this.maxBufferSize) {
      this.buffer = this.buffer.substring(this.buffer.length - this.maxBufferSize);
    }
  }

  /**
   * Get the current buffer content
   */
  getContent(): string {
    return this.buffer;
  }

  /**
   * Clear the buffer
   */
  clear(): void {
    this.buffer = '';
  }

  /**
   * Trim the buffer to keep only the most recent content
   * @param sizeToKeep Number of characters to keep
   */
  trim(sizeToKeep: number = 500): void {
    if (this.buffer.length > sizeToKeep) {
      this.buffer = this.buffer.substring(Math.max(0, this.buffer.length - sizeToKeep));
    }
  }

  /**
   * Cleans terminal output by removing spinner animations and repetitive package checks
   * Optimized for error detection and display
   * @param output The terminal output to clean
   * @returns Cleaned terminal output for error context
   */
  cleanTerminalForErrors(output: string): string {
    console.log('Cleaning terminal output for error detection');

    try {
      // First use the main implementation from shell.ts
      const cleaned = cleanTerminalOutput(output);

      /*
       * Extra error-specific processing to improve error detection
       * Already clean enough from main implementation, but could add more
       * error-specific processing here if needed in the future
       */

      return cleaned;
    } catch (error) {
      console.error('Error in error-specific terminal cleaning:', error);
      return output;
    }
  }

  /**
   * @deprecated Use cleanTerminalForErrors instead
   */
  cleanTerminalOutput(output: string): string {
    console.log('Using deprecated cleanTerminalOutput in ErrorBuffer, please update to use cleanTerminalForErrors');
    return this.cleanTerminalForErrors(output);
  }

  /**
   * Extracts the most relevant error message from terminal output
   * @param output The terminal output to extract error from
   * @returns The most relevant error message
   */
  extractErrorMessage(output: string): string {
    console.log('Extracting most relevant error message');

    try {
      // First clean the output
      const cleaned = this.cleanTerminalForErrors(output);

      // Look for specific error patterns
      const errorMatch = cleaned.match(/error[^:\n]+:?\s*([^\n]+)/i);

      if (errorMatch) {
        return errorMatch[0].trim();
      }

      // Look for "command not found" errors
      const notFoundMatch = cleaned.match(/command not found:[^\n]+/i);

      if (notFoundMatch) {
        return notFoundMatch[0].trim();
      }

      // Default to a short excerpt of the output
      return cleaned
        .split('\n')
        .filter((line) => line.trim())
        .slice(-3)
        .join('\n');
    } catch (error) {
      console.error('Error extracting error message:', error);
      return output.substring(0, 200); // Return truncated output as fallback
    }
  }

  /**
   * Extracts error lines from the buffer to provide better context
   */
  extractErrorLines(): string {
    console.log('Extracting error lines from buffer');

    try {
      const lines = this.buffer.split('\n');
      const errorLines: string[] = [];
      let foundError = false;
      let stackFrames = 0;

      for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i];

        // Detect beginning of error
        if (
          !foundError &&
          (/error|failed|Error|Failed|\[vite\]|TypeError|ReferenceError|SyntaxError/i.test(line) ||
            /at\s+[\w.]+\s+\(/i.test(line))
        ) {
          console.log('Found error start at line:', i);
          foundError = true;
        }

        // Track stack frames
        if (foundError && /at\s+[\w.]+\s+\(/i.test(line)) {
          stackFrames++;
        }

        // Collect lines during error
        if (foundError) {
          errorLines.unshift(line);
        }

        // Stop after collecting before-error context
        if (foundError && stackFrames >= 4 && !/at\s+[\w.]+\s+\(/i.test(line)) {
          break;
        }

        // Collect some context from before the error
        if (foundError && errorLines.length > 15) {
          break;
        }
      }

      console.log('Extracted error lines:', errorLines.length);

      // Clean the extracted error lines
      const extractedContent = errorLines.join('\n');
      return this.cleanTerminalForErrors(extractedContent);
    } catch (error) {
      console.error('Error extracting error lines:', error);
      return this.cleanTerminalForErrors(this.buffer);
    }
  }

  /**
   * Checks if the buffer contains a shell prompt
   * @returns True if a shell prompt is detected
   */
  hasShellPrompt(): boolean {
    return /~\/project\s*\n❯/.test(this.buffer);
  }
}
